import React, { useEffect, useState } from 'react';
import { Box, Paper, Checkbox, FormControlLabel, Button, useTheme, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateGuardian } from '../../hooks/guardians/useCreateGuardian';
import { useUpdateGuardian } from '../../hooks/guardians/useUpdateGuardian';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const FormAddEditGuardian = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const isArabic = i18n.language === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { createGuardian, isPending: isCreating } = useCreateGuardian();
    const { editGuardian, isUpdate } = useUpdateGuardian();
    const { isLoading: schoolsLoading, schools } = useGetSchools(1, 999);

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        school_id: '', full_name: '', phone: '', whatsapp: '',
        address: '', relationship: '', is_active: true,
    });

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            school_id: currentData.school_id || '',
            full_name: currentData.full_name || '',
            phone: currentData.phone || '',
            whatsapp: currentData.whatsapp || '',
            address: currentData.address || '',
            relationship: currentData.relationship || '',
            is_active: currentData.is_active ?? true,
        });
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') { setForm((p) => ({ ...p, [name]: checked })); return; }
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            school_id: form.school_id ? Number(form.school_id) : null,
            full_name: form.full_name,
            phone: form.phone || null,
            whatsapp: form.whatsapp || null,
            address: form.address || null,
            relationship: form.relationship || null,
            is_active: !!form.is_active,
        };
        if (isEdit && currentData?.id) { editGuardian({ req: payload, id: currentData.id }); }
        else { createGuardian(payload); }
    };

    if (schoolsLoading) return <LoadingPage />;

    const schoolOpts = [
        { id: '', name: isArabic ? 'بدون مدرسة' : 'None' },
        ...(schools || []).map((s) => ({ id: s.id, name: s.name })),
    ];

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput label={isArabic ? 'الاسم الكامل' : 'Full Name'}
                            placeholder={isArabic ? 'ادخل الاسم' : 'Enter full name'}
                            type="text" name="full_name" value={form.full_name} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormInput label={isArabic ? 'صلة القرابة' : 'Relationship'}
                            placeholder={isArabic ? 'مثال: أب، أم، عم' : 'e.g. Father, Mother'}
                            type="text" name="relationship" value={form.relationship} onChange={handleChange} required={false} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الهاتف' : 'Phone'}
                            type="text" name="phone" value={form.phone} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'واتساب' : 'WhatsApp'}
                            type="text" name="whatsapp" value={form.whatsapp} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المدرسة' : 'School'} type="select" name="school_id"
                            value={form.school_id}
                            onChange={(e) => setForm((p) => ({ ...p, school_id: e.target.value }))}
                            options={schoolOpts} required={false} />
                    </Grid>

                    <Grid item xs={12}>
                        <FormInput label={isArabic ? 'العنوان' : 'Address'}
                            type="text" name="address" value={form.address} onChange={handleChange} required={false} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <FormControlLabel
                                control={<Checkbox checked={form.is_active} name="is_active" onChange={handleChange} size="small" />}
                                label={isArabic ? 'مفعّل' : 'Active'} />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit}>
                        {loadingSubmit ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : isEdit ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'حفظ' : 'Save')}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditGuardian;
